import { FC, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { i18nTB } from 'utils/shared/utils';
import { TripPreviewSlotView } from 'utils/shared/tripResultBuilderPreview';
import {
  buildTripBuilderRouteStops,
  buildTripBuilderStaticMapImageUrl,
  geocodeRouteLabel,
  loadStaticMapImageSrc,
  normalizeRouteCity,
  resolveTripBuilderRouteStops,
} from 'utils/shared/tripBuilderRoute';
import { MainContext } from 'contexts/MainContext';

type TripBuilderGetPdfRouteProps = {
  slots: TripPreviewSlotView[];
  proposal: ProposalData;
};

const TripBuilderGetPdfRoute: FC<TripBuilderGetPdfRouteProps> = ({ slots, proposal }) => {
  const { initSiteData } = useContext(MainContext);
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const stops = useMemo(() => buildTripBuilderRouteStops(slots, proposal), [slots, proposal]);
  const [geocodedCoords, setGeocodedCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  const [mapImageSrc, setMapImageSrc] = useState('');
  const requestedGeocodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    requestedGeocodesRef.current = new Set();

    stops
      .filter((stop) => stop.lat == null || stop.lng == null)
      .forEach((stop) => {
        const key = normalizeRouteCity(stop.label);
        if (!key || requestedGeocodesRef.current.has(key)) {
          return;
        }
        requestedGeocodesRef.current.add(key);

        void geocodeRouteLabel(stop.label).then((coords) => {
          if (!coords || cancelled) {
            return;
          }
          setGeocodedCoords((prev) => ({
            ...prev,
            [key]: coords,
          }));
        });
      });

    return () => {
      cancelled = true;
    };
  }, [stops]);

  const resolvedStops = useMemo(
    () => resolveTripBuilderRouteStops(stops, geocodedCoords),
    [stops, geocodedCoords],
  );

  const mapImageUrl = useMemo(() => {
    const apiKey = initSiteData?.apiKey || 'AIzaSyD19602oRyuR_70LcX0pKvJctZw7aELmgs';
    if (!apiKey) {
      return '';
    }
    return buildTripBuilderStaticMapImageUrl(resolvedStops, {
      apiKey,
      width: 800,
      height: 340,
    });
  }, [resolvedStops, initSiteData?.apiKey]);

  useEffect(() => {
    let cancelled = false;
    setMapImageSrc('');

    if (!mapImageUrl) {
      return undefined;
    }

    void loadStaticMapImageSrc(mapImageUrl)
      .then((src) => {
        if (!cancelled) {
          setMapImageSrc(src);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [mapImageUrl]);

  if (!stops.length) {
    return null;
  }

  return (
    <div className="tbp-section">
      <div className="tbp-section-label">{t('route')}</div>
      {!!mapImageSrc && (
        <img
          className="tbp-route-map"
          src={mapImageSrc}
          alt=""
          referrerPolicy="no-referrer"
        />
      )}
      <div className="tbp-route-summary">
        {stops.map((stop, index) => (
          <Fragment key={`${stop.label}-${index}`}>
            {index > 0 && <span className="tbp-route-summary-arrow">→</span>}
            <span className="tbp-route-summary-label">{stop.label}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default TripBuilderGetPdfRoute;
